import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TableHead,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CalendarTodayOutlined,
  DownloadOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import * as XLSX from "xlsx";

import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import { GetMonthlyPunchDetails } from "../../../services/asai";
import { GetAllLeaveType } from "../../../services/asaiadmin";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getCurrentMonth = () => new Date().getMonth() + 1;
const getCurrentYear = () => new Date().getFullYear();
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const cleanText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "null" || text === "-") return fallback;
  return text;
};

const getDayNumber = (day) => {
  const provided = Number(day?.dayNumber ?? day?.DayNumber ?? 0);
  if (provided >= 1 && provided <= 31) return provided;

  const date = cleanText(day?.date ?? day?.Date);
  const isoMatch = date.match(/^\d{4}-\d{2}-(\d{2})/);
  if (isoMatch) {
    const number = Number(isoMatch[1]);
    if (number >= 1 && number <= 31) return number;
  }

  // Also accept dd/MM/yyyy and dd-MM-yyyy responses from older APIs.
  const dmyMatch = date.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmyMatch) {
    const number = Number(dmyMatch[1]);
    if (number >= 1 && number <= 31) return number;
  }

  return 0;
};

const normalizeNullableNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatLeaveBalance = (value) =>
  value === null || value === undefined ? "-" : Number(value).toFixed(2);

const LEAVE_BALANCE_COLUMNS = [
  { key: "openingEL", label: "EL Opening" },
  { key: "usedEL", label: "EL Used" },
  { key: "closingEL", label: "EL Closing" },
  { key: "openingCL", label: "CL Opening" },
  { key: "usedCL", label: "CL Used" },
  { key: "closingCL", label: "CL Closing" },
  { key: "openingSL", label: "SL Opening" },
  { key: "usedSL", label: "SL Used" },
  { key: "closingSL", label: "SL Closing" },
];

const normalizeNullableBoolean = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "y", "active"].includes(normalized)) return true;
    if (["0", "false", "no", "n", "inactive"].includes(normalized))
      return false;
  }
  return null;
};

const normalizeEmployeeType = (value) => {
  // Keep the complete backend source label (e.g. Payroll CNC / Payroll FLEX)
  // so the Employee Type column distinguishes CNC and FLEX categories.
  return cleanText(value, "-");
};

const normalizePunchResponse = (response) => {
  const root = response?.data?.data ?? response?.data ?? response ?? {};
  const rawRows = Array.isArray(root?.result)
    ? root.result
    : Array.isArray(root?.Result)
    ? root.Result
    : Array.isArray(root?.employees)
    ? root.employees
    : Array.isArray(root?.Employees)
    ? root.Employees
    : Array.isArray(response?.data)
    ? response.data
    : [];

  // The backend applies Status = 1. Keep this defensive filter as well when
  // EmployeeStatusCode is present so stale/cached API payloads cannot add other statuses.
  const eligibleRows = rawRows.filter((row) => {
    const statusCode =
      row?.employeeStatusCode ??
      row?.EmployeeStatusCode ??
      row?.statusCode ??
      row?.StatusCode;
    return Number(statusCode) === 1;
  });

  return eligibleRows.map((row) => {
    const active = normalizeNullableBoolean(row?.active ?? row?.Active);
    const rawDays = Array.isArray(row?.days)
      ? row.days
      : Array.isArray(row?.Days)
      ? row.Days
      : [];
    const days = rawDays.map((day) => ({
      dayNumber: getDayNumber(day),
      date: cleanText(day?.date ?? day?.Date),
      day: cleanText(day?.day ?? day?.Day),
      inTime: cleanText(
        day?.inTime ?? day?.InTime ?? day?.checkin ?? day?.CheckIn,
      ),
      outTime: cleanText(
        day?.outTime ?? day?.OutTime ?? day?.checkout ?? day?.CheckOut,
      ),
      status: cleanText(day?.status ?? day?.Status, "No Data"),
      hours: cleanText(day?.hours ?? day?.Hours),
      overtime: Number(day?.overtime ?? day?.Overtime ?? 0) || 0,
      lateMinutes: Number(day?.lateMinutes ?? day?.LateMinutes ?? 0) || 0,
      earlyMinutes: Number(day?.earlyMinutes ?? day?.EarlyMinutes ?? 0) || 0,
      holidayName: cleanText(day?.holidayName ?? day?.HolidayName),
      leaveReason: cleanText(
        day?.leaveReason ??
          day?.LeaveReason ??
          day?.employeeComment ??
          day?.EmployeeComment,
      ),
      hasLeaveRequest:
        normalizeNullableBoolean(
          day?.hasLeaveRequest ?? day?.HasLeaveRequest,
        ) === true,
      leaveSegment: cleanText(day?.leaveSegment ?? day?.LeaveSegment),
      leaveTypeId: cleanText(day?.leaveTypeId ?? day?.LeaveTypeId),
      leaveType: cleanText(day?.leaveType ?? day?.LeaveType),
      isLOP:
        normalizeNullableBoolean(day?.isLOP ?? day?.IsLOP) === true ||
        cleanText(day?.status ?? day?.Status)
          .trim()
          .toUpperCase() === "LOP",
      lopPortion: Number(day?.lopPortion ?? day?.LopPortion ?? 0) || 0,
      leaveApprovalStatus: cleanText(
        day?.leaveApprovalStatus ?? day?.LeaveApprovalStatus,
      ),
      isLeavePending:
        normalizeNullableBoolean(day?.isLeavePending ?? day?.IsLeavePending) ===
        true,
    }));
    const daysByNumber = new Array(32);
    days.forEach((day) => {
      if (day.dayNumber >= 1 && day.dayNumber <= 31) {
        daysByNumber[day.dayNumber] = day;
      }
    });
    const personId = cleanText(
      row?.personId ?? row?.PersonId ?? row?.personid ?? row?.EmployeeID,
      "-",
    );
    const personName = cleanText(
      row?.personName ?? row?.PersonName ?? row?.name ?? row?.EmployeeName,
      "Unknown employee",
    );
    return {
      personId,
      personName,
      employeeType: normalizeEmployeeType(
        row?.employeeType ??
          row?.EmployeeType ??
          row?.typeOfEmployee ??
          row?.TypeOfEmployee ??
          row?.typeofemployee,
      ),
      organization: cleanText(
        row?.organization ??
          row?.Organization ??
          row?.shift ??
          row?.Shift ??
          row?.shiftName ??
          row?.ShiftName,
        "-",
      ),
      presentDays: Number(row?.presentDays ?? row?.PresentDays ?? 0) || 0,
      absentDays: Number(row?.absentDays ?? row?.AbsentDays ?? 0) || 0,
      totalWorkingDays:
        Number(row?.totalWorkingDays ?? row?.TotalWorkingDays ?? 0) || 0,
      totalWorkingHours:
        Number(row?.totalWorkingHours ?? row?.TotalWorkingHours ?? 0) || 0,
      totalOvertime: Number(row?.totalOvertime ?? row?.TotalOvertime ?? 0) || 0,
      openingEL: normalizeNullableNumber(row?.openingEL ?? row?.OpeningEL),
      usedEL: normalizeNullableNumber(row?.usedEL ?? row?.UsedEL),
      closingEL: normalizeNullableNumber(row?.closingEL ?? row?.ClosingEL),
      openingCL: normalizeNullableNumber(row?.openingCL ?? row?.OpeningCL),
      usedCL: normalizeNullableNumber(row?.usedCL ?? row?.UsedCL),
      closingCL: normalizeNullableNumber(row?.closingCL ?? row?.ClosingCL),
      openingSL: normalizeNullableNumber(row?.openingSL ?? row?.OpeningSL),
      usedSL: normalizeNullableNumber(row?.usedSL ?? row?.UsedSL),
      closingSL: normalizeNullableNumber(row?.closingSL ?? row?.ClosingSL),
      // Assigned weekly holiday from the current monthly shift assignment.
      // This is used for non-office employees; do not infer weekends for them.
      offDay: cleanText(
        row?.offDay ?? row?.OffDay ?? row?.off_day ?? row?.Off_day,
      ),
      department: cleanText(row?.department ?? row?.Department, "-"),
      active,
      employeeStatusCode: normalizeNullableNumber(
        row?.employeeStatusCode ??
          row?.EmployeeStatusCode ??
          row?.statusCode ??
          row?.StatusCode,
      ),
      searchIndex: [
        personId,
        personName,
        row?.employeeType ??
          row?.EmployeeType ??
          row?.typeOfEmployee ??
          row?.TypeOfEmployee,
        row?.organization ??
          row?.Organization ??
          row?.shift ??
          row?.Shift ??
          row?.shiftName ??
          row?.ShiftName,
        row?.department ?? row?.Department,
        row?.offDay ?? row?.OffDay,
      ]
        .map((value) => cleanText(value).toLowerCase())
        .join(" "),
      lastPunchDate: cleanText(row?.lastPunchDate ?? row?.LastPunchDate),
      // Active from emp.UserMaster is the source of truth when available.
      // Backend should populate the compatibility fields below only for older API responses.
      employeeStatus: cleanText(
        row?.employeeStatus ??
          row?.EmployeeStatus ??
          row?.employmentStatus ??
          row?.EmploymentStatus ??
          row?.exitStatus ??
          row?.ExitStatus,
      ),
      exitDate: cleanText(
        row?.exitDate ??
          row?.ExitDate ??
          row?.dateOfLeaving ??
          row?.DateOfLeaving ??
          row?.resignationDate ??
          row?.ResignationDate,
      ),
      isExited: [true, 1, "1", "true", "yes", "y"].includes(
        typeof (
          row?.isExited ??
          row?.IsExited ??
          row?.hasLeftCompany ??
          row?.HasLeftCompany
        ) === "string"
          ? (
              row?.isExited ??
              row?.IsExited ??
              row?.hasLeftCompany ??
              row?.HasLeftCompany
            )
              .trim()
              .toLowerCase()
          : row?.isExited ??
              row?.IsExited ??
              row?.hasLeftCompany ??
              row?.HasLeftCompany,
      ),
      days,
      daysByNumber,
    };
  });
};

const isDefaultMidnightTime = (value) => {
  const normalized = cleanText(value).replace(/\s+/g, " ").trim();
  // VMSAttendance can store midnight as a default value for a missing punch.
  return /^(?:0{1,2}:00(?::00)?|12:00(?::00)?\s*am)$/i.test(normalized);
};

const hasPunchTime = (value) => {
  const normalized = cleanText(value);
  return Boolean(
    normalized && normalized !== "-" && !isDefaultMidnightTime(normalized),
  );
};

// Format device punch values consistently for the hover tooltip. Supports
// time-only strings (HH:mm[:ss]), values with AM/PM, and datetime strings.
const formatTime = (value) => {
  const normalized = cleanText(value);
  if (!normalized || isDefaultMidnightTime(normalized)) return "Not recorded";

  const match = normalized.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);

  if (!match) return normalized;

  let hours = Number(match[1]);
  const minutes = match[2];
  const seconds = match[3] || "00";
  let meridiem = (match[4] || "").toUpperCase();

  if (meridiem === "AM") {
    hours = hours % 12;
  } else if (meridiem === "PM") {
    hours = (hours % 12) + 12;
  } else {
    meridiem = hours >= 12 ? "PM" : "AM";
  }

  const displayHours = hours % 12 || 12;
  return `${String(displayHours).padStart(
    2,
    "0",
  )}:${minutes}:${seconds} ${meridiem}`;
};

// Monthly report cells show attendance status only (never the raw IN/OUT times).
// A one-sided punch is always displayed as M, while the backend's detailed status
// remains the source of truth whenever both punches are present.
const getPunchStatusLabel = (day, hasIn, hasOut) => {
  // The requested list is punch-first: exactly one of IN/OUT means M;
  // both punches mean P. Late/early is represented by the red P styling.
  if (hasIn !== hasOut) return "M";
  if (hasIn && hasOut) return "P";

  // When neither punch exists, preserve backend no-punch classifications.
  const rawStatus = cleanText(day?.status);
  const compactStatus = rawStatus.toUpperCase().replace(/\s+/g, "");
  switch (compactStatus) {
    case "M":
    case "MISSINGPUNCH":
    case "MISSING":
      return "M";
    default:
      return rawStatus;
  }
};

const isGovernmentHoliday = (day) => {
  const status = cleanText(day?.status).toLowerCase();
  return (
    status === "gh" ||
    status === "government holiday" ||
    Boolean(cleanText(day?.holidayName))
  );
};

const isOfficeEmployee = (employee) =>
  cleanText(employee?.employeeType).toLowerCase().startsWith("office employee");

// Do not infer EXIT from missing punches. The backend must provide an explicit employee status,
// an exit flag, or a confirmed date of leaving from the employee master.
const isExitedEmployee = (employee) => {
  if (!employee) return false;
  // The Active bit from emp.UserMaster is authoritative when provided: 0 means EXIT, 1 means active.
  if (typeof employee.active === "boolean") return employee.active === false;
  if (employee.isExited) return true;

  const status = cleanText(employee.employeeStatus)
    .toLowerCase()
    .replace(/[._-]+/g, " ")
    .trim();
  if (
    [
      "exit",
      "exited",
      "left",
      "left company",
      "employee exited",
      "resigned",
      "terminated",
    ].includes(status)
  ) {
    return true;
  }

  const exitDate = cleanText(employee.exitDate);
  if (exitDate) {
    const parsed = new Date(
      exitDate.length === 10 ? `${exitDate}T00:00:00` : exitDate,
    );
    if (!Number.isNaN(parsed.getTime())) {
      parsed.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return parsed <= today;
    }
  }

  return false;
};

const hasLeaveRequest = (day) =>
  Boolean(day && (day.hasLeaveRequest || day.isLeavePending));

const hasFtoRequest = (day) => {
  if (!day) return false;
  const status = cleanText(day.status).toLowerCase();
  return hasLeaveRequest(day) || status.includes("fto");
};

// Keep approval details in the tooltip, but display the shared cell label "Absent / FTO".
// Actual punch times always win over a leave-only cell label.
const isApprovedFto = (day) => {
  if (!day || !hasFtoRequest(day)) return false;
  if (hasPunchTime(day.inTime) || hasPunchTime(day.outTime)) return false;

  const status = cleanText(day.status).toLowerCase();
  const approval = cleanText(day.leaveApprovalStatus).toLowerCase();
  return (
    status === "fto approved" ||
    approval === "approved" ||
    approval === "self-approved"
  );
};

const isPendingFto = (day) => {
  if (!day || !hasFtoRequest(day)) return false;
  if (hasPunchTime(day.inTime) || hasPunchTime(day.outTime)) return false;
  const status = cleanText(day.status).toLowerCase();
  const approval = cleanText(day.leaveApprovalStatus).toLowerCase();
  if (status.includes("pending") || approval.includes("pending")) return true;
  return !isApprovedFto(day);
};

const getTodayDateKey = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(today.getDate()).padStart(2, "0")}`;
};

// If no punch and no FTO exist, mark a past working date Absent. Do not mark
// future dates (or today's incomplete attendance) absent solely because no punch has arrived.
const isAbsentDay = (day, reportYear, reportMonth, employee) => {
  if (!day) return false;

  // An applicable FTO or LeaveSheet request is not an absent day.
  if (hasLeaveRequest(day) || hasFtoRequest(day)) return false;
  if (isGovernmentHoliday(day) || isOffDay(day, employee)) return false;
  if (hasPunchTime(day.inTime) || hasPunchTime(day.outTime)) return false;

  const status = cleanText(day.status).toLowerCase();
  if (status === "a" || status === "absent") return true;

  const dateKey = getAttendanceDateKey(day, reportYear, reportMonth);
  if (!dateKey) return false;
  return dateKey < getTodayDateKey();
};

const getAttendanceDateKey = (day, reportYear, reportMonth) => {
  const dateValue = cleanText(day?.date ?? day?.Date);
  if (dateValue) return normalizeDateKey(dateValue);

  const dayNumber = Number(day?.dayNumber ?? day?.DayNumber);
  if (!dayNumber || dayNumber < 1 || dayNumber > 31) return "";

  const date = new Date(Number(reportYear), Number(reportMonth) - 1, dayNumber);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

// Mark no-punch working days as EXIT only after the employee's latest real punch.
// If the employee is inactive but no real punch date exists, the inactive master
// record is the only authoritative signal, so no-punch working dates show EXIT.
// Real punches, FTO, GH and WH always retain their own display.
const normalizeDateKey = (value) => {
  const text = cleanText(value);
  if (!text) return "";

  // .NET normally returns ISO dates, but also handle common SQL/UI date formats.
  const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];

  const dmyMatch = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmyMatch) {
    const day = Number(dmyMatch[1]);
    const month = Number(dmyMatch[2]);
    const year = Number(dmyMatch[3]);
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
        2,
        "0",
      )}`;
    }
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(parsed.getDate()).padStart(2, "0")}`;
};

const isExitDate = (employee, day, reportYear, reportMonth) => {
  if (!day) return false;
  if (hasPunchTime(day.inTime) || hasPunchTime(day.outTime)) return false;
  if (
    hasLeaveRequest(day) ||
    hasFtoRequest(day) ||
    isGovernmentHoliday(day) ||
    isOffDay(day, employee)
  )
    return false;

  // Prefer the backend's day-level EXIT classification. This avoids a UI-side
  // date-format comparison disagreeing with the repository's LastPunchDate logic.
  if (cleanText(day.status).toLowerCase() === "exit") return true;

  if (!isExitedEmployee(employee)) return false;

  const attendanceDate = normalizeDateKey(
    getAttendanceDateKey(day, reportYear, reportMonth),
  );
  if (!attendanceDate) return false;

  const lastPunchDate = normalizeDateKey(employee.lastPunchDate);
  if (lastPunchDate) return attendanceDate > lastPunchDate;

  // Backward-compatible fallback for inactive employees if history has no real punch.
  return true;
};

const getDayTooltipTitle = (employee, day, leaveTypeMap = {}) => {
  const hasIn = hasPunchTime(day?.inTime);
  const hasOut = hasPunchTime(day?.outTime);
  const hasPunch = hasIn || hasOut;
  const governmentHoliday = !hasPunch && isGovernmentHoliday(day);
  const hasLeave = Boolean(
    day?.hasLeaveRequest ||
      day?.leaveSegment ||
      day?.leaveReason ||
      day?.leaveTypeId ||
      day?.leaveType,
  );
  const leaveTypeId = cleanText(day?.leaveTypeId);
  const rawStatus = cleanText(day?.status);
  const compactStatus = rawStatus.toUpperCase().replace(/\s+/g, "");
  const lateMinutes = Math.max(0, Number(day?.lateMinutes) || 0);
  const earlyMinutes = Math.max(0, Number(day?.earlyMinutes) || 0);
  const lateFlag =
    lateMinutes > 0 || ["PL", "P-L", "P-L+P-E"].includes(compactStatus);
  const earlyFlag =
    earlyMinutes > 0 || ["PE", "P-E", "P-L+P-E"].includes(compactStatus);
  const displayStatus = governmentHoliday
    ? "GH"
    : !hasPunch && day?.isLOP
    ? Number(day?.lopPortion) >= 0.999
      ? "LOP"
      : Number(day?.lopPortion) >= 0.49 && Number(day?.lopPortion) <= 0.51
      ? "1/2 LOP"
      : Number(day?.lopPortion) >= 0.24 && Number(day?.lopPortion) <= 0.26
      ? "1/4 LOP"
      : "LOP"
    : !hasPunch &&
      isOfficeEmployee(employee) &&
      (rawStatus.toLowerCase().includes("fto") ||
        (day?.hasLeaveRequest && !leaveTypeId))
    ? "A - FTO"
    : !hasPunch && !isOfficeEmployee(employee) && day?.hasLeaveRequest
    ? "A - Leave Sheet"
    : hasPunch
    ? "P"
    : rawStatus;
  const isFto =
    isOfficeEmployee(employee) &&
    (cleanText(day?.status).toLowerCase().includes("fto") ||
      cleanText(day?.leaveType).toUpperCase() === "FTO" ||
      (day?.hasLeaveRequest && !leaveTypeId));
  const leaveType =
    cleanText(day?.leaveType) ||
    (leaveTypeId ? cleanText(leaveTypeMap[leaveTypeId]) : "") ||
    (isFto ? "FTO" : leaveTypeId ? `Leave Type #${leaveTypeId}` : "");
  const leaveReason = cleanText(day?.leaveReason);

  return (
    <Box sx={{ maxWidth: 330, minWidth: 210, whiteSpace: "normal", py: 0.35 }}>
      <Typography sx={{ fontSize: "0.76rem", fontWeight: 800, mb: 0.65 }}>
        Attendance Details
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "82px 1fr",
          columnGap: 1,
          rowGap: 0.35,
        }}
      >
        <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
          Status
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 800 }}>
          {displayStatus || (hasIn || hasOut ? "P" : "No attendance")}
        </Typography>
        {hasPunch &&
          rawStatus &&
          ![
            "P",
            "PRESENT",
            "PL",
            "P-L",
            "PE",
            "P-E",
            "P-L+P-E",
            "PH",
            "P-H",
            "P+LUNCH",
            "P+LUNCHBREAK",
            "P+SL",
          ].includes(compactStatus) && (
            <>
              <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
                Rule Status
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
                {rawStatus}
              </Typography>
            </>
          )}
        {lateFlag && (
          <>
            <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
              Late
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
              {lateMinutes > 0 ? `${lateMinutes} minute(s)` : "Late arrival"}
            </Typography>
          </>
        )}
        {earlyFlag && (
          <>
            <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
              Early Exit
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
              {earlyMinutes > 0
                ? `${earlyMinutes} minute(s)`
                : "Left before shift end"}
            </Typography>
          </>
        )}
        <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
          IN Time
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
          {hasIn ? formatTime(day?.inTime) : "Missing / Not recorded"}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
          OUT Time
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
          {hasOut ? formatTime(day?.outTime) : "Missing / Not recorded"}
        </Typography>
        {day?.hours && (
          <>
            <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
              Worked
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
              {day.hours}
            </Typography>
          </>
        )}
      </Box>

      {governmentHoliday && (
        <Box sx={{ mt: 0.75 }}>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.18)", mb: 0.6 }} />
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, mb: 0.25 }}>
            Government Holiday (GH)
          </Typography>
          <Typography sx={{ fontSize: "0.7rem" }}>
            {cleanText(day?.holidayName, "Government Holiday")}
          </Typography>
        </Box>
      )}

      {hasLeave && (
        <Box sx={{ mt: 0.75 }}>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.18)", mb: 0.6 }} />
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, mb: 0.4 }}>
            Leave Details
          </Typography>
          {leaveType && (
            <Typography sx={{ fontSize: "0.7rem", mb: 0.25 }}>
              <Box component="span" sx={{ fontWeight: 800 }}>
                Type of Leave:
              </Box>{" "}
              {leaveType}
            </Typography>
          )}
          {day?.leaveSegment && (
            <Typography sx={{ fontSize: "0.7rem", mb: 0.25 }}>
              <Box component="span" sx={{ fontWeight: 800 }}>
                Leave Duration:
              </Box>{" "}
              {day.leaveSegment}
            </Typography>
          )}
          {Number(day?.lopPortion) > 0 && (
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, mb: 0.25 }}>
              <Box component="span" sx={{ fontWeight: 800 }}>
                LOP Portion:
              </Box>{" "}
              {Number(day.lopPortion).toFixed(2)} day(s)
            </Typography>
          )}
          {day?.leaveApprovalStatus && (
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, mb: 0.25 }}>
              <Box component="span" sx={{ fontWeight: 800 }}>
                Approval:
              </Box>{" "}
              {day.leaveApprovalStatus}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: "0.7rem",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            <Box component="span" sx={{ fontWeight: 800 }}>
              Reason:
            </Box>{" "}
            {leaveReason || "Not provided"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const normalizeWeekday = (value) => {
  const text = cleanText(value)
    .toLowerCase()
    .replace(/[.,]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return "";

  const weekdays = {
    mon: "mon",
    monday: "mon",
    tue: "tue",
    tues: "tue",
    tuesday: "tue",
    wed: "wed",
    wednesday: "wed",
    thu: "thu",
    thur: "thu",
    thurs: "thu",
    thursday: "thu",
    fri: "fri",
    friday: "fri",
    sat: "sat",
    saturday: "sat",
    sun: "sun",
    sunday: "sun",
  };

  return weekdays[text] || weekdays[text.slice(0, 3)] || "";
};

// These values may occur in stored attendance-status rows. For non-office employees,
// they are considered a weekly holiday only when there is no roster OffDay.
const WEEKLY_OFF_STATUSES = new Set([
  "o",
  "op",
  "wh",
  "off day",
  "offday",
  "weekly holiday",
  "weekly off",
]);

const isWeeklyOffStatus = (day) =>
  WEEKLY_OFF_STATUSES.has(cleanText(day?.status).toLowerCase());

const isOffDay = (day, employee) => {
  if (!day) return false;

  const status = cleanText(day.status).toLowerCase();
  const attendanceWeekday = normalizeWeekday(day.day);

  // Office employee weekend rule remains Saturday/Sunday.
  if (isOfficeEmployee(employee)) {
    return (
      WEEKLY_OFF_STATUSES.has(status) ||
      ["sat", "sun"].includes(attendanceWeekday)
    );
  }

  // Non-office employees must match their own assigned weekday from ShiftImportRecords.
  // If OffDay is present, do not let a stale O/OP/WH status mark another weekday off.
  const configuredOffDay = normalizeWeekday(employee?.offDay);
  if (configuredOffDay) {
    return Boolean(attendanceWeekday) && configuredOffDay === attendanceWeekday;
  }

  // Compatibility for older API responses that do not include OffDay.
  // Never assume Saturday/Sunday for non-office employees without an assigned OffDay.
  return WEEKLY_OFF_STATUSES.has(status);
};

const getDayPresentation = (employee, day, reportYear, reportMonth) => {
  const hasIn = hasPunchTime(day?.inTime);
  const hasOut = hasPunchTime(day?.outTime);
  const hasPunch = hasIn || hasOut;

  // A no-punch holiday is GH. If either punch exists, show the actual
  // punch-derived status (P/PL/PH/PE/M) while keeping HolidayName available
  // to the hover tooltip.
  const governmentHoliday = !hasPunch && isGovernmentHoliday(day);
  const scheduledOffDay = isOffDay(day, employee);
  const offDay = !hasPunch && !governmentHoliday && scheduledOffDay;
  const workedOnOffDay = hasPunch && !governmentHoliday && scheduledOffDay;
  const rawStatus = cleanText(day?.status);
  const status = rawStatus.toLowerCase();
  const compactStatus = rawStatus.toUpperCase().replace(/\s+/g, "");
  const lateMinutes = Math.max(0, Number(day?.lateMinutes) || 0);
  const earlyMinutes = Math.max(0, Number(day?.earlyMinutes) || 0);
  const lateFlag =
    lateMinutes > 0 || ["PL", "P-L", "P-L+P-E"].includes(compactStatus);
  const earlyFlag =
    earlyMinutes > 0 || ["PE", "P-E", "P-L+P-E"].includes(compactStatus);
  const isLateOrEarlyPunch = hasPunch && (lateFlag || earlyFlag);
  const lopPortion = Math.max(0, Number(day?.lopPortion) || 0);
  const isLOP =
    !hasPunch &&
    !governmentHoliday &&
    (day?.isLOP === true || status === "lop" || lopPortion > 0);
  const lopLabel =
    lopPortion >= 0.999
      ? "LOP"
      : lopPortion >= 0.49 && lopPortion <= 0.51
      ? "1/2 LOP"
      : lopPortion >= 0.24 && lopPortion <= 0.26
      ? "1/4 LOP"
      : lopPortion > 0
      ? `${lopPortion.toFixed(2)} LOP`
      : "LOP";

  // FTO is for office employees; LeaveSheet is used for other employee types.
  const officeEmployee = isOfficeEmployee(employee);
  const hasFto =
    officeEmployee &&
    !hasPunch &&
    !governmentHoliday &&
    !offDay &&
    hasFtoRequest(day);
  const hasOtherLeave =
    !officeEmployee &&
    !hasPunch &&
    !governmentHoliday &&
    !offDay &&
    !isLOP &&
    (hasLeaveRequest(day) ||
      cleanText(day?.status).toLowerCase().includes("leave"));
  const pendingFto = hasFto && isPendingFto(day);
  const approvedFto = hasFto && !pendingFto && isApprovedFto(day);
  const exitAbsence = isExitDate(employee, day, reportYear, reportMonth);
  const absent =
    !hasPunch &&
    !governmentHoliday &&
    !offDay &&
    !hasFto &&
    !hasOtherLeave &&
    !isLOP &&
    !exitAbsence &&
    isAbsentDay(day, reportYear, reportMonth, employee);

  const punchStatus = hasPunch ? getPunchStatusLabel(day, hasIn, hasOut) : "";
  const showOtherStatus =
    !hasPunch &&
    !governmentHoliday &&
    !absent &&
    !offDay &&
    !hasFto &&
    !hasOtherLeave &&
    !isLOP &&
    !exitAbsence &&
    !isWeeklyOffStatus(day) &&
    !(isOfficeEmployee(employee) === false && status.includes("fto")) &&
    status &&
    !["no data", "-", "p", "present"].includes(status);

  let label = "-";
  let background = "transparent";
  let color = "#64748b";
  let fontWeight = 500;

  if (governmentHoliday) {
    label = "GH";
    background = "#ede9fe";
    color = "#5b21b6";
    fontWeight = 800;
  } else if (isLOP) {
    label = lopLabel;
    background = "#fce7f3";
    color = "#9d174d";
    fontWeight = 800;
  } else if (hasFto) {
    label = "A - FTO";
    background = "#ffebee";
    color = "#c62828";
    fontWeight = 800;
  } else if (hasOtherLeave) {
    label = "A - Leave Sheet";
    background = "#ffebee";
    color = "#c62828";
    fontWeight = 800;
  } else if (exitAbsence) {
    label = "EXIT";
    background = "#fff3b0";
    color = "#795900";
    fontWeight = 900;
  } else if (absent) {
    label = "A";
    background = "#ffebee";
    color = "#c62828";
    fontWeight = 800;
  } else if (offDay) {
    label = "WH";
    background = "#f2f4f7";
    color = "#697586";
    fontWeight = 700;
  } else if (hasPunch) {
    label = punchStatus || "P";
    if (label === "M") {
      background = "#fff8e1";
      color = "#9a6700";
      fontWeight = 800;
    } else if (label === "A") {
      background = "#ffebee";
      color = "#c62828";
      fontWeight = 800;
    } else if (label === "H") {
      background = "#dbeafe";
      color = "#1d4ed8";
      fontWeight = 800;
    } else if (isLateOrEarlyPunch) {
      // Show the requested simple P label in red when IN is late or OUT is early.
      label = "P";
      background = "#fee2e2";
      color = "#b91c1c";
      fontWeight = 900;
    } else {
      label = "P";
      background = "#dcfce7";
      color = "#166534";
      fontWeight = 800;
    }
  } else if (showOtherStatus) {
    label = getPunchStatusLabel(day, hasIn, hasOut) || rawStatus || "-";
    background = "#fff8e1";
    color =
      status === "m" || status.includes("missing") ? "#9a6700" : "#64748b";
    if (label === "PH") {
      background = "#dbeafe";
      color = "#1d4ed8";
      fontWeight = 800;
    } else if (label === "PL") {
      background = "#fef3c7";
      color = "#92400e";
      fontWeight = 800;
    } else if (label === "PE") {
      background = "#ffedd5";
      color = "#c2410c";
      fontWeight = 800;
    } else if (label.toLowerCase().includes("lunch")) {
      background = "#e0f2fe";
      color = "#0369a1";
      fontWeight = 800;
    }
  }

  return {
    hasIn,
    hasOut,
    hasPunch,
    governmentHoliday,
    offDay,
    scheduledOffDay,
    workedOnOffDay,
    isLOP,
    hasFto,
    hasOtherLeave,
    pendingFto,
    approvedFto,
    exitAbsence,
    absent,
    showOtherStatus,
    punchStatus,
    isLateOrEarlyPunch,
    lateMinutes,
    earlyMinutes,
    label,
    background,
    color,
    fontWeight,
  };
};

const formatDayCount = (value) => {
  const number = Number(value) || 0;
  return Number.isInteger(number) ? String(number) : number.toFixed(1);
};

const formatHourTotal = (value) => {
  const totalMinutes = Math.max(0, Math.round((Number(value) || 0) * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};

const initials = (name) =>
  cleanText(name, "E")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const getDayHeader = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  return {
    dayNumber: day,
    weekday: date.toLocaleDateString("en-IN", { weekday: "short" }),
    dateLabel: `${String(day).padStart(2, "0")} ${MONTHS[month - 1].slice(
      0,
      3,
    )}`,
  };
};

const DAY_STATUS_FILTERS = [
  { value: "", label: "All statuses", shortLabel: "All" },
  { value: "present", label: "Present (both punches)", shortLabel: "Present" },
  { value: "missing", label: "Missing Punch", shortLabel: "Missing" },
  { value: "wh", label: "Weekly Holiday (WH)", shortLabel: "WH" },
  { value: "gh", label: "Government Holiday (GH)", shortLabel: "GH" },
  { value: "fto", label: "Absent / FTO", shortLabel: "FTO" },
  { value: "leave", label: "Absent / LeaveSheet", shortLabel: "Leave" },
  { value: "lop", label: "Leave Without Pay (LOP)", shortLabel: "LOP" },
  { value: "exit", label: "EXIT", shortLabel: "EXIT" },
  { value: "absent", label: "A (Absent)", shortLabel: "A" },
  { value: "other", label: "Other status", shortLabel: "Other" },
  { value: "no-data", label: "No Data / blank", shortLabel: "No Data" },
];

const createEmptyColumnFilters = () => ({
  personId: "",
  personName: "",
  employeeType: "",
  organization: "",
  dayStatuses: {},
});

const getDayFilterCategory = (employee, day, reportYear, reportMonth) => {
  const presentation = getDayPresentation(
    employee,
    day,
    reportYear,
    reportMonth,
  );
  const status = cleanText(day?.status).toLowerCase();

  if (presentation.governmentHoliday) return "gh";
  if (presentation.isLOP) return "lop";
  if (presentation.hasFto) return "fto";
  if (presentation.hasOtherLeave) return "leave";
  if (presentation.exitAbsence) return "exit";
  if (presentation.absent) return "absent";
  if (presentation.offDay) return "wh";

  if (presentation.hasPunch) {
    return presentation.hasIn && presentation.hasOut ? "present" : "missing";
  }

  if (
    status === "m" ||
    status.includes("missing punch") ||
    status.includes("missing")
  ) {
    return "missing";
  }

  if (presentation.showOtherStatus) return "other";
  return "no-data";
};

const filterTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 32,
    borderRadius: 1.25,
    backgroundColor: "#ffffff",
    fontSize: "0.72rem",
    transition: "background-color 120ms ease, border-color 120ms ease",
    "& fieldset": { borderColor: "#d8e1eb" },
    "&:hover fieldset": { borderColor: "#f6a184" },
    "&.Mui-focused fieldset": {
      borderColor: "#ff8b6a",
      borderWidth: 1.5,
    },
  },
  "& input": {
    px: 0.9,
    py: 0.5,
    color: "#334155",
    "&::placeholder": { color: "#94a3b8", opacity: 1 },
  },
};

const filterSelectSx = (isActive) => ({
  height: 32,
  borderRadius: 1.25,
  backgroundColor: isActive ? "#fff4ef" : "#ffffff",
  color: isActive ? "#9a3412" : "#475569",
  fontSize: "0.7rem",
  transition: "background-color 120ms ease, border-color 120ms ease",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: isActive ? "#f6a184" : "#d8e1eb",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#f6a184",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ff8b6a",
    borderWidth: 1.5,
  },
  "& .MuiSelect-select": {
    py: 0.55,
    pl: 0.8,
    pr: "24px !important",
    minHeight: "unset",
  },
  "& .MuiSelect-icon": { fontSize: 18, right: 3, color: "#64748b" },
});

const MonthlyPunchReport = () => {
  const [month, setMonth] = useState(getCurrentMonth());
  const [year, setYear] = useState(getCurrentYear());
  const [rows, setRows] = useState([]);
  const [leaveTypeMap, setLeaveTypeMap] = useState({});
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState(createEmptyColumnFilters);
  const [appliedColumnFilters, setAppliedColumnFilters] = useState(
    createEmptyColumnFilters,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Mouse-drag scrolling for the wide monthly punch table.
  const tableContainerRef = useRef(null);
  const tableDragRef = useRef({ startX: 0, scrollLeft: 0, moved: false });
  const [isTableDragging, setIsTableDragging] = useState(false);

  const handleTableMouseDown = (event) => {
    if (event.button !== 0) return;

    // Keep normal interaction available for filters and other controls inside
    // the sticky table header. Dragging starts only on the table surface/cells.
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest(
        'input, textarea, select, button, a, [role="button"], [role="combobox"], .MuiInputBase-root, .MuiSelect-select, .MuiIconButton-root',
      )
    ) {
      return;
    }

    const container = tableContainerRef.current;
    if (!container || container.scrollWidth <= container.clientWidth) return;

    tableDragRef.current = {
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
      moved: false,
    };
    setIsTableDragging(true);
    // Prevent text selection while the user is dragging the table.
    event.preventDefault();
  };

  useEffect(() => {
    if (!isTableDragging) return undefined;

    const handleMouseMove = (event) => {
      const container = tableContainerRef.current;
      if (!container) return;

      const deltaX = event.clientX - tableDragRef.current.startX;
      if (Math.abs(deltaX) > 3) tableDragRef.current.moved = true;
      if (tableDragRef.current.moved) {
        container.scrollLeft = tableDragRef.current.scrollLeft - deltaX;
      }
    };

    const handleMouseUp = () => setIsTableDragging(false);

    // Listen on document so a drag continues even when the pointer leaves the
    // visible table area before the mouse button is released.
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isTableDragging]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 180);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAppliedColumnFilters(columnFilters);
    }, 120);
    return () => clearTimeout(timeoutId);
  }, [columnFilters]);

  const daysInMonth = useMemo(() => getDaysInMonth(year, month), [year, month]);
  const dayHeaders = useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, index) =>
        getDayHeader(year, month, index + 1),
      ),
    [daysInMonth, month, year],
  );

  const handleLoad = async () => {
    if (
      !Number.isInteger(Number(year)) ||
      Number(year) < 2000 ||
      Number(year) > 2100
    ) {
      setError("Please enter a valid year.");
      return;
    }
    if (
      !Number.isInteger(Number(month)) ||
      Number(month) < 1 ||
      Number(month) > 12
    ) {
      setError("Please select a valid month.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await GetMonthlyPunchDetails({
        month: Number(month),
        year: Number(year),
      });
      const parsed = normalizePunchResponse(response).sort((a, b) =>
        a.personName.localeCompare(b.personName),
      );
      setRows(parsed);
      setSearchText("");
      setColumnFilters(createEmptyColumnFilters());
      setAppliedColumnFilters(createEmptyColumnFilters());
      setPage(0);
      if (parsed.length === 0) {
        setError(
          "No employee punch records were found for the selected month.",
        );
      }
    } catch (requestError) {
      console.error("Failed to load monthly punch details:", requestError);
      setRows([]);
      setError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.Message ||
          requestError?.message ||
          "Unable to load monthly punch details.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadLeaveTypes = async () => {
      try {
        const response = await GetAllLeaveType();
        const root = response?.data?.data ?? response?.data ?? response ?? [];
        const leaveTypes = Array.isArray(root)
          ? root
          : Array.isArray(root?.result)
          ? root.result
          : Array.isArray(root?.Result)
          ? root.Result
          : [];

        const map = {};
        leaveTypes.forEach((item) => {
          const id =
            item?.id ?? item?.Id ?? item?.leaveTypeId ?? item?.LeaveTypeId;
          const name = cleanText(
            item?.leaveType ?? item?.LeaveType ?? item?.name ?? item?.Name,
          );
          if (id !== null && id !== undefined && name) map[String(id)] = name;
        });

        if (isMounted) setLeaveTypeMap(map);
      } catch (leaveTypeError) {
        // Attendance and punch details should continue to load if the optional
        // leave-type lookup is temporarily unavailable.
        console.error("Failed to load leave type names:", leaveTypeError);
      }
    };

    void loadLeaveTypes();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    void handleLoad();
    // Load the current month/year on the first render, matching the Monthly Attendance page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    const search = debouncedSearchText.trim().toLowerCase();
    const idFilter = appliedColumnFilters.personId.trim().toLowerCase();
    const nameFilter = appliedColumnFilters.personName.trim().toLowerCase();
    const typeFilter = appliedColumnFilters.employeeType.trim().toLowerCase();
    const organizationFilter = appliedColumnFilters.organization
      .trim()
      .toLowerCase();
    const dayStatusFilters = Object.entries(
      appliedColumnFilters.dayStatuses,
    ).filter(([, value]) => Boolean(value));

    return rows.filter((employee) => {
      const matchesGlobalSearch =
        !search ||
        (employee.searchIndex || "").includes(search) ||
        cleanText(employee.employeeStatus).toLowerCase().includes(search) ||
        cleanText(employee.exitDate).toLowerCase().includes(search) ||
        (isExitedEmployee(employee) ? "exit" : "active").includes(search);

      if (!matchesGlobalSearch) return false;
      if (
        idFilter &&
        !cleanText(employee.personId).toLowerCase().includes(idFilter)
      )
        return false;
      if (
        nameFilter &&
        !cleanText(employee.personName).toLowerCase().includes(nameFilter)
      )
        return false;
      if (
        typeFilter &&
        !cleanText(employee.employeeType).toLowerCase().includes(typeFilter)
      )
        return false;
      if (
        organizationFilter &&
        !cleanText(employee.organization)
          .toLowerCase()
          .includes(organizationFilter)
      )
        return false;

      if (dayStatusFilters.length > 0) {
        const matchesEveryDayFilter = dayStatusFilters.every(
          ([dayNumber, selectedStatus]) => {
            const day = employee.daysByNumber?.[Number(dayNumber)];
            return (
              getDayFilterCategory(employee, day, year, month) ===
              selectedStatus
            );
          },
        );
        if (!matchesEveryDayFilter) return false;
      }

      return true;
    });
  }, [rows, debouncedSearchText, appliedColumnFilters, year, month]);

  const updateColumnFilter = (key, value) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
    setPage(0);
  };

  const updateDayStatusFilter = (dayNumber, value) => {
    setColumnFilters((current) => {
      const dayStatuses = { ...current.dayStatuses };
      if (value) dayStatuses[dayNumber] = value;
      else delete dayStatuses[dayNumber];
      return { ...current, dayStatuses };
    });
    setPage(0);
  };

  const hasActiveColumnFilters = Boolean(
    columnFilters.personId ||
      columnFilters.personName ||
      columnFilters.employeeType ||
      columnFilters.organization ||
      Object.values(columnFilters.dayStatuses).some(Boolean),
  );

  const pagedRows = useMemo(() => {
    // Keep the number of rendered rows bounded; large employee lists remain
    // responsive through pagination instead of creating tens of thousands of cells.
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1,
    );

    if (page > maxPage) setPage(maxPage);
  }, [filteredRows.length, page, rowsPerPage]);

  const handleExportExcel = () => {
    if (!filteredRows.length) {
      setError("There is no data to export.");
      return;
    }

    const identityColumns = [
      "Person ID",
      "Employee Name",
      "Employee Type",
      "Organization / Shift",
    ];
    const summaryColumns = [
      "Present Days",
      "Absent Days",
      "Total Working Days",
      "Total Working Hours",
      "Total Overtime",
      ...LEAVE_BALANCE_COLUMNS.map((column) => column.label),
    ];
    const firstHeader = [...identityColumns];
    const secondHeader = Array(identityColumns.length).fill("");
    dayHeaders.forEach((header) => {
      firstHeader.push(header.dateLabel, "");
      secondHeader.push("Status", "Holiday / Leave Details");
    });
    summaryColumns.forEach((label) => {
      firstHeader.push(label);
      secondHeader.push("");
    });

    const exportRows = filteredRows.map((employee) => {
      const output = [
        employee.personId,
        employee.personName,
        employee.employeeType,
        employee.organization,
      ];
      dayHeaders.forEach(({ dayNumber }) => {
        const day = employee.daysByNumber?.[dayNumber];
        const presentation = getDayPresentation(employee, day, year, month);
        const leaveDetails = [
          day?.holidayName,
          day?.leaveSegment,
          day?.leaveApprovalStatus,
          day?.leaveReason,
        ]
          .map((value) => cleanText(value))
          .filter(Boolean)
          .join(" — ");

        let displayStatus;
        if (presentation.governmentHoliday) displayStatus = "GH";
        else if (presentation.isLOP)
          displayStatus = presentation.label || "LOP";
        else if (presentation.hasFto) displayStatus = "A - FTO";
        else if (presentation.hasOtherLeave) displayStatus = "A - Leave Sheet";
        else if (presentation.exitAbsence) displayStatus = "EXIT";
        else if (presentation.absent) displayStatus = "A";
        else if (presentation.offDay) displayStatus = "WH";
        else if (presentation.hasPunch)
          displayStatus = presentation.label || "P";
        else {
          const rawStatus = cleanText(day?.status, "No Data");
          const staleWeeklyOff = isWeeklyOffStatus(day) && !presentation.offDay;
          const nonOfficeFto =
            !isOfficeEmployee(employee) &&
            rawStatus.toLowerCase().includes("fto");
          displayStatus =
            staleWeeklyOff || nonOfficeFto ? "No Data" : rawStatus;
        }

        output.push(displayStatus, leaveDetails);
      });
      output.push(
        employee.presentDays,
        employee.absentDays,
        employee.totalWorkingDays,
        employee.totalWorkingHours,
        employee.totalOvertime,
        ...LEAVE_BALANCE_COLUMNS.map((column) =>
          employee[column.key] === null || employee[column.key] === undefined
            ? ""
            : employee[column.key],
        ),
      );
      return output;
    });

    const worksheet = XLSX.utils.aoa_to_sheet([
      [`Employee Punch Details - ${MONTHS[month - 1]} ${year}`],
      [],
      firstHeader,
      secondHeader,
      ...exportRows,
    ]);
    const mergeRanges = Array.from(
      { length: identityColumns.length },
      (_, columnIndex) => ({
        s: { r: 2, c: columnIndex },
        e: { r: 3, c: columnIndex },
      }),
    );
    dayHeaders.forEach((_, index) => {
      const startColumn = identityColumns.length + index * 2;
      mergeRanges.push({
        s: { r: 2, c: startColumn },
        e: { r: 2, c: startColumn + 1 },
      });
    });
    summaryColumns.forEach((_, index) => {
      const column = identityColumns.length + dayHeaders.length * 2 + index;
      mergeRanges.push({
        s: { r: 2, c: column },
        e: { r: 3, c: column },
      });
    });
    worksheet["!merges"] = mergeRanges;
    worksheet["!cols"] = [
      { wch: 14 },
      { wch: 28 },
      { wch: 23 },
      { wch: 24 },
      ...dayHeaders.flatMap(() => [{ wch: 14 }, { wch: 32 }]),
      { wch: 13 },
      { wch: 13 },
      { wch: 18 },
      { wch: 20 },
      { wch: 17 },
      ...LEAVE_BALANCE_COLUMNS.map(() => ({ wch: 14 })),
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Punch Details");
    XLSX.writeFile(
      workbook,
      `OfficeEmployeePunchDetails_${MONTHS[month - 1]}_${year}.xlsx`,
    );
  };

  const breadcrumbItems = [
    { to: "/", title: "ASAI" },
    { title: "Employee Punch Details" },
  ];

  const tableMinWidth =
    700 + (5 + LEAVE_BALANCE_COLUMNS.length) * 110 + daysInMonth * 112;

  return (
    <PageContainer title="Employee Punch Details">
      <Breadcrumb title="Employee Punch Details" items={breadcrumbItems} />

      <Box
        sx={{
          width: "100%",
          px: { xs: 1, sm: 1.5, md: 2.5 },
          py: { xs: 1, md: 1.5 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            mb: 1.5,
          }}
        >
          <CardContent
            sx={{
              p: { xs: 1.25, md: 1.5 },
              "&:last-child": { pb: { xs: 1.25, md: 1.5 } },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <FormControl
                size="small"
                sx={{ width: { xs: "100%", sm: 180, md: 205 }, flexShrink: 0 }}
              >
                <InputLabel>Month</InputLabel>
                <Select
                  value={month}
                  label="Month"
                  onChange={(event) => setMonth(Number(event.target.value))}
                >
                  {MONTHS.map((monthName, index) => (
                    <MenuItem key={monthName} value={index + 1}>
                      {monthName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                type="number"
                label="Year"
                value={year}
                onChange={(event) => setYear(Number(event.target.value))}
                sx={{ width: { xs: "100%", sm: 120 }, flexShrink: 0 }}
                InputProps={{
                  startAdornment: (
                    <CalendarTodayOutlined
                      sx={{ mr: 0.75, fontSize: 17, color: "text.secondary" }}
                    />
                  ),
                }}
              />

              <Button
                size="small"
                variant="contained"
                onClick={handleLoad}
                disabled={loading}
                sx={{
                  minWidth: 95,
                  height: 40,
                  px: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                Search
              </Button>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  ml: { xs: 0, xl: "auto" },
                  flex: { xs: "1 1 100%", md: "0 0 auto" },
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search employee..."
                  value={searchText}
                  onChange={(event) => {
                    setSearchText(event.target.value);
                    setPage(0);
                  }}
                  sx={{ width: { xs: "100%", sm: 220, md: 245 } }}
                  InputProps={{
                    startAdornment: (
                      <SearchOutlined
                        sx={{ mr: 0.5, fontSize: 18, color: "text.secondary" }}
                      />
                    ),
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setColumnFilters(createEmptyColumnFilters());
                    setSearchText("");
                    setPage(0);
                  }}
                  disabled={!searchText && !hasActiveColumnFilters}
                  sx={{
                    height: 40,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Clear Filters
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadOutlined />}
                  onClick={handleExportExcel}
                  disabled={!filteredRows.length}
                  sx={{
                    minWidth: 130,
                    height: 40,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Export Excel
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "error.light",
              bgcolor: "error.lighter",
              color: "error.main",
              borderRadius: 1.5,
              px: 1.5,
              py: 1,
              mb: 1.5,
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            {error}
          </Box>
        )}

        {loading ? (
          <></>
        ) : rows.length > 0 ? (
          <Card
            elevation={0}
            sx={{
              overflow: "hidden",
            }}
          >
            <TableContainer
              ref={tableContainerRef}
              onMouseDown={handleTableMouseDown}
              sx={{
                maxHeight: 660,
                overflowX: "auto",
                overflowY: "auto",
                // Force a consistent grab cursor across table text, cells and
                // nested typography. Interactive controls keep their own cursors.
                cursor: `${isTableDragging ? "grabbing" : "grab"} !important`,
                "& *": {
                  cursor: `${isTableDragging ? "grabbing" : "grab"} !important`,
                },
                "& input, & textarea": { cursor: "text !important" },
                '& button, & a, & [role="button"], & [role="combobox"], & .MuiIconButton-root':
                  {
                    cursor: "pointer !important",
                  },
                userSelect: isTableDragging ? "none" : "auto",
                WebkitUserSelect: isTableDragging ? "none" : "auto",
                touchAction: "pan-y",
                "&::-webkit-scrollbar": { height: 10, width: 10 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#b8c2ce",
                  borderRadius: 10,
                  border: "2px solid #f8fafc",
                },
                "&::-webkit-scrollbar-track": { backgroundColor: "#f8fafc" },
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: tableMinWidth,
                  tableLayout: "fixed",
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    color: "#334155",
                    borderColor: "#dce4ed",
                    verticalAlign: "middle",
                  },
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    borderColor: "#e2e8f0",
                  },
                  "& .MuiTableCell-root": {
                    borderRight: "1px solid",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontSize: "0.75rem",
                    py: 0.7,
                    px: 0.7,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      height: 54,
                      "& .MuiTableCell-root": {
                        background:
                          "linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%)",
                        borderBottom: "1px solid #d7e0ea",
                        boxShadow: "inset 0 1px 0 #ffffff",
                        py: 0.9,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        top: 0,
                        zIndex: 10,
                        bgcolor: "#f3f6fa",
                        minWidth: 100,
                        width: 100,
                        color: "#1e293b",
                        fontWeight: 800,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Person ID
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 100,
                        top: 0,
                        zIndex: 10,
                        bgcolor: "#f3f6fa",
                        minWidth: 220,
                        width: 220,
                        color: "#1e293b",
                        fontWeight: 800,
                        letterSpacing: "0.01em",
                      }}
                    >
                      Employee Name
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 8,
                        minWidth: 170,
                        width: 170,
                        fontWeight: 800,
                        color: "#1e293b",
                        letterSpacing: "0.01em",
                        bgcolor: "#f3f6fa",
                      }}
                    >
                      Employee Type
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 8,
                        minWidth: 190,
                        width: 190,
                        fontWeight: 800,
                        color: "#1e293b",
                        letterSpacing: "0.01em",
                        bgcolor: "#f3f6fa",
                      }}
                    >
                      Organization / Shift
                    </TableCell>
                    {dayHeaders.map((header) => (
                      <TableCell
                        key={header.dayNumber}
                        align="center"
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 8,
                          minWidth: 112,
                          width: 112,
                          bgcolor: "#f3f6fa",
                          color: "#1e293b",
                          fontWeight: 800,
                          borderLeft: "1px solid #e1e8f0",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            lineHeight: 1.25,
                            gap: 0.15,
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              color: "#64748b",
                              textTransform: "uppercase",
                              letterSpacing: "0.035em",
                            }}
                          >
                            {header.weekday}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.74rem",
                              fontWeight: 800,
                              color: "#1e293b",
                            }}
                          >
                            {header.dateLabel}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                    {[
                      "Present Days",
                      "Absent Days",
                      "Total Working Days",
                      "Total Working Hours",
                      "Total Overtime",
                      ...LEAVE_BALANCE_COLUMNS.map((column) => column.label),
                    ].map((label) => (
                      <TableCell
                        key={label}
                        align="center"
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 8,
                          minWidth: 110,
                          width: 110,
                          bgcolor: "#f3f6fa",
                          color: "#1e293b",
                          fontWeight: 800,
                          whiteSpace: "normal",
                          lineHeight: 1.2,
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow
                    sx={{
                      height: 46,
                      "& .MuiTableCell-root": {
                        backgroundColor: "#fbfcfe",
                        py: 0.7,
                        borderBottom: "2px solid #dce6f0",
                        boxShadow: "0 2px 0 rgba(15, 23, 42, 0.025)",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        top: 54,
                        zIndex: 9,
                        minWidth: 100,
                        width: 100,
                        bgcolor: "#fbfcfe",
                      }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        value={columnFilters.personId}
                        onChange={(event) =>
                          updateColumnFilter("personId", event.target.value)
                        }
                        placeholder="Filter ID"
                        inputProps={{ "aria-label": "Filter Person ID" }}
                        sx={filterTextFieldSx}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 100,
                        top: 54,
                        zIndex: 9,
                        minWidth: 220,
                        width: 220,
                        bgcolor: "#fbfcfe",
                      }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        value={columnFilters.personName}
                        onChange={(event) =>
                          updateColumnFilter("personName", event.target.value)
                        }
                        placeholder="Filter name"
                        inputProps={{ "aria-label": "Filter Employee Name" }}
                        sx={filterTextFieldSx}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        top: 54,
                        zIndex: 7,
                        minWidth: 170,
                        width: 170,
                        bgcolor: "#fbfcfe",
                      }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        value={columnFilters.employeeType}
                        onChange={(event) =>
                          updateColumnFilter("employeeType", event.target.value)
                        }
                        placeholder="Filter type"
                        inputProps={{ "aria-label": "Filter Employee Type" }}
                        sx={filterTextFieldSx}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        top: 54,
                        zIndex: 7,
                        minWidth: 190,
                        width: 190,
                        bgcolor: "#fbfcfe",
                      }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        value={columnFilters.organization}
                        onChange={(event) =>
                          updateColumnFilter("organization", event.target.value)
                        }
                        placeholder="Filter shift"
                        inputProps={{
                          "aria-label": "Filter Organization or Shift",
                        }}
                        sx={filterTextFieldSx}
                      />
                    </TableCell>
                    {dayHeaders.map((header) => (
                      <TableCell
                        key={`filter-${header.dayNumber}`}
                        align="center"
                        sx={{
                          position: "sticky",
                          top: 54,
                          zIndex: 7,
                          minWidth: 112,
                          width: 112,
                          bgcolor: "#fbfcfe",
                          px: 0.55,
                          borderLeft: "1px solid #edf1f5",
                        }}
                      >
                        <FormControl size="small" fullWidth>
                          <Select
                            value={
                              columnFilters.dayStatuses[header.dayNumber] || ""
                            }
                            displayEmpty
                            onChange={(event) =>
                              updateDayStatusFilter(
                                header.dayNumber,
                                event.target.value,
                              )
                            }
                            inputProps={{
                              "aria-label": `Filter ${header.dateLabel} status`,
                            }}
                            renderValue={(selected) => {
                              const option = DAY_STATUS_FILTERS.find(
                                (item) => item.value === selected,
                              );
                              return option?.shortLabel || "All";
                            }}
                            sx={filterSelectSx(
                              Boolean(
                                columnFilters.dayStatuses[header.dayNumber],
                              ),
                            )}
                          >
                            {DAY_STATUS_FILTERS.map((option) => (
                              <MenuItem
                                key={option.value || "all-statuses"}
                                value={option.value}
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    ))}
                    {Array.from({ length: 5 }, (_, index) => (
                      <TableCell
                        key={`summary-filter-${index}`}
                        align="center"
                        sx={{
                          position: "sticky",
                          top: 54,
                          zIndex: 7,
                          minWidth: 110,
                          width: 110,
                          bgcolor: "#fbfcfe",
                        }}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((employee) => {
                    const employeeExited = isExitedEmployee(employee);
                    const exitedRowColor = "#fff3b0";
                    return (
                      <TableRow
                        hover
                        key={employee.personId}
                        sx={
                          employeeExited
                            ? {
                                "& > .MuiTableCell-root": {
                                  bgcolor: exitedRowColor,
                                },
                                "&:hover > .MuiTableCell-root": {
                                  bgcolor: "#ffe780",
                                },
                              }
                            : undefined
                        }
                      >
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            bgcolor: employeeExited
                              ? exitedRowColor
                              : "background.paper",
                            fontWeight: 700,
                            minWidth: 100,
                            width: 100,
                          }}
                        >
                          {employee.personId}
                        </TableCell>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 100,
                            zIndex: 3,
                            bgcolor: employeeExited
                              ? exitedRowColor
                              : "background.paper",
                            minWidth: 220,
                            width: 220,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.8,
                            }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "action.hover",
                                color: "primary.main",
                                fontSize: "0.68rem",
                                fontWeight: 800,
                                flexShrink: 0,
                              }}
                            >
                              {initials(employee.personName)}
                            </Box>
                            <Typography
                              noWrap
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.77rem",
                                maxWidth: 175,
                              }}
                              title={employee.personName}
                            >
                              {employee.personName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            minWidth: 170,
                            bgcolor: employeeExited
                              ? exitedRowColor
                              : "inherit",
                          }}
                        >
                          {employee.employeeType || "-"}
                        </TableCell>
                        <TableCell
                          sx={{
                            minWidth: 190,
                            bgcolor: employeeExited
                              ? exitedRowColor
                              : "inherit",
                          }}
                        >
                          {employee.organization || "-"}
                        </TableCell>
                        {dayHeaders.map(({ dayNumber }) => {
                          const day = employee.daysByNumber?.[dayNumber];
                          const presentation = getDayPresentation(
                            employee,
                            day,
                            year,
                            month,
                          );
                          const { background, color, fontWeight, label } =
                            presentation;
                          // Every day cell can be hovered to inspect punches and any leave details.
                          const cell = (
                            <Box
                              component="span"
                              sx={{
                                minHeight: 38,
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                px: 0.55,
                                py: 0.25,
                                borderRadius: 0.5,
                                // Preserve normal punch/GH/WH/FTO colors; only post-last-punch exit dates are yellow.
                                bgcolor: background,
                                color,
                                fontWeight,
                                fontSize: "0.68rem",
                                lineHeight: 1.45,
                                cursor: "help",
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: fontWeight || 700,
                                  color: "inherit",
                                  whiteSpace: "normal",
                                  textAlign: "center",
                                  lineHeight: 1.35,
                                }}
                              >
                                {label || "-"}
                              </Typography>
                            </Box>
                          );

                          return (
                            <TableCell
                              key={`${employee.personId}-${dayNumber}`}
                              align="center"
                              sx={{ minWidth: 112, width: 112, px: 0.35 }}
                            >
                              <Tooltip
                                arrow
                                placement="top"
                                enterDelay={250}
                                title={getDayTooltipTitle(
                                  employee,
                                  day,
                                  leaveTypeMap,
                                )}
                              >
                                {cell}
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                        <TableCell
                          align="center"
                          sx={{
                            minWidth: 110,
                            fontWeight: 800,
                            color: "#166534",
                          }}
                        >
                          {formatDayCount(employee.presentDays)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            minWidth: 110,
                            fontWeight: 800,
                            color: "#b91c1c",
                          }}
                        >
                          {formatDayCount(employee.absentDays)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ minWidth: 110, fontWeight: 700 }}
                        >
                          {formatDayCount(employee.totalWorkingDays)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            minWidth: 110,
                            fontWeight: 800,
                            color: "#1d4ed8",
                          }}
                        >
                          {formatHourTotal(employee.totalWorkingHours)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            minWidth: 110,
                            fontWeight: 800,
                            color: "#7c3aed",
                          }}
                        >
                          {formatHourTotal(employee.totalOvertime)}
                        </TableCell>
                        {LEAVE_BALANCE_COLUMNS.map((column) => (
                          <TableCell
                            key={`${employee.personId}-${column.key}`}
                            align="center"
                            sx={{
                              minWidth: 110,
                              fontWeight: 700,
                              color: column.key.startsWith("used")
                                ? "#b45309"
                                : column.key.startsWith("closing")
                                ? "#166534"
                                : "#334155",
                              bgcolor: column.key.startsWith("closing")
                                ? "#f0fdf4"
                                : "inherit",
                            }}
                          >
                            {formatLeaveBalance(employee[column.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                  {pagedRows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={9 + LEAVE_BALANCE_COLUMNS.length + daysInMonth}
                        align="center"
                        sx={{ py: 5 }}
                      >
                        No employees match the current search and column
                        filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50]}
              sx={{ "& .MuiTablePagination-toolbar": { minHeight: 44 } }}
            />
          </Card>
        ) : (
          <Box
            sx={{
              minHeight: 280,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.paper",
              p: 3,
            }}
          >
            <CalendarTodayOutlined
              sx={{ fontSize: 42, mb: 1, color: "text.secondary" }}
            />
            <Typography fontWeight={700}>Employee Punch Details</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, textAlign: "center" }}
            >
              Select a month and year to view daily attendance statuses for all
              employee types.
            </Typography>
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};

export default MonthlyPunchReport;
